import { omit, pickBy, mapValues, has, isNull, isUndefined, isObject, isArray, get, find, assign  } from "lodash";
import { AssignmentConfig, AssignmentData } from "../interfaces/assignment";
import { LeaveRequestConfig, LeaveRequestsData } from "../interfaces/leave-request";
import { AuthorConfig, AuthorData } from "../interfaces/author";
import { RoleConfig, RoleData } from "../interfaces/role";
import { DepartmentConfig, DepartmentData } from "../interfaces/department";
import { Injectable } from "@angular/core";
import { PersonnelComponent } from "../../pages/personnel/personnel.component";
import { PersonConfig, PersonData } from "../interfaces/person";
import { TrainingConfig, TrainingData, TrainingResultConfig, TrainingResultData } from "../interfaces/training";
import { OKRData, AppraisalData, AppraisalConfig, OKRConfig } from "../interfaces/performance";
import { JobPostingConfig, JobPostingData } from "../interfaces/job-posting";
import { ApplicationConfig, ApplicationData } from "../interfaces/application";
import { TaskConfig, TaskData } from "../interfaces/task";
import { BookConfig, BookData } from "../interfaces/book";
import { ReviewConfig, ReviewData } from "../interfaces/review";
import { InternalDocConfig, InternalDocData } from "../interfaces/internal-doc";
import { PayrollPeriodConfig, PayrollPeriodData } from "../interfaces/payroll-period";
import { SalaryStructureConfig, SalaryStructureData } from "../interfaces/salary-structure";
import { PayrollCycleConfig, PayrollCycleData } from "../interfaces/payroll-cycle";
import { UserConfig, UserData } from "../interfaces/auth/users";
import { UsersApi } from "../api/users.api";
import { UserStore } from "../stores/user.store";
import { EntityFieldConfig } from "../interfaces/common/entity-config.interface";
import { FieldAccessConfig, OptionsByAccessEntry } from "../interfaces/common/field-access.interface";

@Injectable()
export class EntityService  {

  constructor(
    private assignmentService: AssignmentData,
    private leaveRequestsData: LeaveRequestsData,
    private roleData: RoleData,
    private departmentData: DepartmentData,
    private personnelData: PersonData,
    private trainingData: TrainingData,
    private trainingResultData: TrainingResultData,
    private okrData: OKRData,
    private appraisalData: AppraisalData,
    private jobPostingData: JobPostingData,
    private applicationData: ApplicationData,
    private taskData: TaskData,
    private bookData: BookData,
    private authorData: AuthorData,
    private reviewData: ReviewData,
    private internalDocData: InternalDocData,
    private payrollPeriodData: PayrollPeriodData,
    private payrollCycleData: PayrollCycleData,
    private salaryStructureData: SalaryStructureData,
    private userApi: UsersApi,
    private userStore: UserStore,
  ) {}

  get entityMapping() {
    const entityMapping = [
      { name: 'assignment', config: AssignmentConfig, data: this.assignmentService },
      { name: 'leave-request', config: LeaveRequestConfig, data: this.leaveRequestsData },
      { name: 'role', alias: ['role.title'], config: RoleConfig, data: this.roleData },
      { name: 'department', alias: ['department.name'], config: DepartmentConfig, data: this.departmentData },
      { name: 'person', alias: ['personnel', 'reviewer', 'attendees'], config: PersonConfig, data: this.personnelData },
      { name: 'training', config: TrainingConfig, data: this.trainingData},
      { name: 'training-result', config: TrainingResultConfig, data: this.trainingResultData},
      { name: 'appraisal', config: AppraisalConfig, data: this.appraisalData},
      { name: 'okr', config: OKRConfig, data: this.okrData},
      { name: 'job-posting', alias: ['posting'], config: JobPostingConfig, data: this.jobPostingData},
      { name: 'application', config: ApplicationConfig, data: this.applicationData},
      { name: 'task', config: TaskConfig, data: this.taskData},
      { name: 'book', alias: ['books'], config: BookConfig, data: this.bookData},
      { name: 'author', config: AuthorConfig, data: this.authorData},
      { name: 'review', alias:['reviews'], config: ReviewConfig, data: this.reviewData},
      { name: 'internal-doc', alias:['internal-document'], config: InternalDocConfig, data: this.internalDocData}, // To be implemented
      { name: 'payroll period', config: PayrollPeriodConfig, data: this.payrollPeriodData },
      { name: 'payroll cycle', config: PayrollCycleConfig, data: this.payrollCycleData },
      { name: 'salary-structure', config: SalaryStructureConfig, data: this.salaryStructureData },
      { name: 'user', alias: ['users'], config: UserConfig, data: this.userApi },
    ];

    return entityMapping;
  }

  private findEntityByName(entityName: string) {
    const key = String(entityName || '').toLowerCase();
    return this.entityMapping.find(e => {
      if (String(e.name).toLowerCase() === key) return true;
      if (Array.isArray((e as any).alias)) {
        return (e as any).alias.some((a: any) => String(a).toLowerCase() === key);
      }
      return false;
    });
  }

  getEntityConfigByName(entityName: string){
    const theEntity = this.findEntityByName(entityName);
    if (!theEntity) {
      throw new Error(`No config found for entity: ${entityName}`);
    }
    return theEntity.config;
  }

  getEntityDataByName(entityName: string): { [key: string]: any } {
    const theEntity = this.findEntityByName(entityName)
    if (!theEntity) {
      throw new Error(`No data found for entity: ${entityName}`);
    }
    return theEntity.data;
  }

  /**
   * Evaluates a declarative field access rule (from an EntityFieldConfig's
   * `visible`/`editable`, or a FormFieldConfig's `visible`/`editable`)
   * against the currently logged-in user.
   */
  checkFieldAccess(rule: FieldAccessConfig | undefined): boolean {
    if (rule === undefined || rule === true) return true;
    if (rule === false) return false;
    return this.userStore.isAuthorizedFor(
      rule.requiredRoles ?? null,
      rule.requiredPermissions ?? null,
      rule.combinationMode ?? 'both',
    );
  }

  isFieldVisible(field: EntityFieldConfig): boolean {
    return this.checkFieldAccess(field?.visible);
  }

  isFieldEditable(field: EntityFieldConfig): boolean {
    return this.checkFieldAccess(field?.editable);
  }

  /**
   * Whether the user may change this field in editors.
   * Always honors `editable` first (default true). When `optionsByAccess` is
   * present, also requires a matching entry with at least one option.
   */
  isFieldInteractivelyEditable(field: {
    editable?: FieldAccessConfig;
    optionsByAccess?: OptionsByAccessEntry[];
  }): boolean {
    if (!this.checkFieldAccess(field?.editable)) {
      return false;
    }
    if (field?.optionsByAccess?.length) {
      const match = field.optionsByAccess.find(entry => this.checkFieldAccess(entry.access));
      return Array.isArray(match?.options) && match!.options.length > 0;
    }
    return true;
  }

  /**
   * Option list for the current user.
   * Prefers first matching `optionsByAccess` entry; otherwise `options`.
   */
  getFieldOptions(field: EntityFieldConfig): Array<{ value: any; title: string }> | undefined {
    if (field?.optionsByAccess?.length) {
      const match = field.optionsByAccess.find(entry => this.checkFieldAccess(entry.access));
      return match?.options ?? field.options;
    }
    return field?.options;
  }
}